<script setup lang="ts">
import { computed } from 'vue';
import type { PatientAllergy } from '@/api/medications';
import ClinicalAuthorLine from '@/components/clinical/clinical-author-line.vue';

// The patient's allergies to read: those kept, "Sin alergias conocidas", or a
// warning when nobody recorded them. They are edited in
// medication-allergies-dialog. Without them (the request failed) it never
// claims there are none.
const props = defineProps<{ allergies?: PatientAllergy[]; loading?: boolean }>();

const kept = computed(() => (props.allergies ?? []).filter((row) => row.kind === 'allergy'));
const noneKnown = computed(() => props.allergies?.find((row) => row.kind === 'none_known') ?? null);
</script>
<template>
  <v-progress-circular
    v-if="loading"
    class="align-self-center"
    color="primary"
    indeterminate
  />
  <v-alert
    v-else-if="!allergies"
    type="error"
    variant="tonal"
    text="No se pudieron cargar las alergias. Recarga la página para intentarlo de nuevo."
  />
  <v-list
    v-else-if="kept.length > 0"
    lines="two"
    density="compact"
    class="py-0"
  >
    <v-list-item
      v-for="allergy in kept"
      :key="allergy.id"
      class="px-0"
    >
      <v-list-item-title>
        {{ allergy.name }}
        <v-chip
          v-if="allergy.severity !== 'unknown'"
          :color="allergy.severity === 'severe' ? 'error' : 'warning'"
          size="x-small"
          variant="tonal"
          class="ml-1"
        >
          {{ allergy.humanSeverity }}
        </v-chip>
      </v-list-item-title>
      <v-list-item-subtitle>
        <span v-if="allergy.reaction">{{ allergy.reaction }}</span>
        <clinical-author-line
          :author="allergy.author"
          :at="allergy.humanCreatedAt"
        />
      </v-list-item-subtitle>
    </v-list-item>
  </v-list>
  <div
    v-else-if="noneKnown"
    class="d-flex align-center ga-2"
  >
    <span class="text-body-2">Sin alergias conocidas</span>
    <clinical-author-line
      :author="noneKnown.author"
      :at="noneKnown.humanCreatedAt"
    />
  </div>
  <v-alert
    v-else
    type="warning"
    variant="tonal"
    density="compact"
    title="Alergias no registradas"
    text="Nadie ha indicado si el paciente tiene alergias."
  />
</template>
